import { Link } from "react-router-dom";
import { agentFormatsPath, useApi } from "../api/client";
import type { AgentFormatsResponse } from "../api/types";
import { Empty, ErrorState, Loading } from "./States";
import { fmtDate, fmtTokens } from "../lib/format";

/**
 * The document templates this agent has seen, worst-scoring first.
 *
 * A document type is `trade_licence`; a format is the Abu Dhabi ADDED layout as
 * distinct from the Dubai DED one. Until formats existed, a problem confined to
 * one issuer's paperwork showed up only as a slightly lower average across the
 * whole agent — and an average is not something anyone can act on.
 *
 * Sorted by required-field score ascending, because the question this answers is
 * which paperwork the agent is bad at, and that is where a reviewer's time is
 * worth spending.
 */
export function AgentFormats({ documentType }: { documentType: string }) {
  const { data, error, loading, reload } = useApi<AgentFormatsResponse>(
    agentFormatsPath(documentType),
  );

  if (loading)
    return (
      <div className="card">
        <Loading what="formats" />
      </div>
    );
  if (error)
    return (
      <div className="card">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  if (data.formats.length === 0) {
    return (
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Document formats</span>
        </div>
        <div className="card-body">
          <Empty
            title="No formats recognised yet"
            hint="Formats are derived when a document is extracted. Anything ingested before this was added is fingerprinted by scripts/backfill_document_formats.py."
          />
        </div>
      </div>
    );
  }

  const scored = data.formats.filter((f) => f.required_score !== null);
  const worst = scored[0];
  const best = scored[scored.length - 1];
  const spread =
    worst && best && worst.format_id !== best.format_id
      ? Math.round(((best.required_score ?? 0) - (worst.required_score ?? 0)) * 10) / 10
      : null;

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Document formats</span>
        <span className="muted">
          {data.total_formats} template(s) across {fmtTokens(data.assigned_documents)}{" "}
          document(s)
        </span>
      </div>
      <div className="card-body">
        <p className="page-sub" style={{ marginTop: 0 }}>
          The same document type printed by different issuers. A format is derived
          from the issuing authority and the numbering scheme — never from the
          values on the page.
        </p>

        {/* The headline the agent-level average cannot give: the same agent
            reading one issuer's paperwork perfectly and another's badly. */}
        {spread !== null && spread >= 20 ? (
          <div className="notice notice-warn">
            This agent's score is not uniform. <strong>{best.name}</strong> scores{" "}
            {best.required_score}% while <strong>{worst.name}</strong> scores{" "}
            {worst.required_score}% — a {spread}-point spread that the agent-level
            average hides.
          </div>
        ) : null}

        {!data.requirements_defined ? (
          <div className="notice">
            No fields are scored for this agent, so formats can only be counted,
            not scored. Choose them on the Fields tab.
          </div>
        ) : null}

        {/* Said before the table, not after it. A column of em dashes is easy
            to read as "these templates were not tested" when what it means is
            "nobody has checked what these templates actually say". */}
        {data.scored_formats === 0 ? (
          <div className="notice notice-warn">
            <strong>No format has an accuracy figure yet.</strong> Accuracy compares an
            extraction against what a person read off the document, and no document of any
            of these {data.total_formats} formats has been verified. The Required fields and
            Avg confidence columns are populated and useful, but neither can tell you a value
            was <em>right</em> — only that one was produced.
            <div style={{ marginTop: 8 }}>
              Verifying a handful of the worst-scoring formats is what turns this column on;
              they are listed first.
            </div>
          </div>
        ) : (
          <div className="notice">
            Accuracy covers <strong>{data.scored_formats}</strong> of {data.total_formats} formats
            ({data.verified_fields} verified fields). The rest show — because nothing of that
            template has been verified, not because they scored zero.
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Format</th>
                <th scope="col" className="num">Documents</th>
                <th
                  scope="col"
                  className="num"
                  title="Agreement with human-verified ground truth for this format. The only accuracy here; everything else measures whether a value appeared, not whether it was right."
                >
                  Accuracy
                </th>
                <th
                  scope="col"
                  className="num"
                  title="Share of this format's documents where every scored field was produced. Measures presence, not correctness."
                >
                  Required fields
                </th>
                <th scope="col">Weakest field</th>
                <th scope="col" className="num">Avg confidence</th>
                <th scope="col" className="num">Verified</th>
                <th scope="col">Last seen</th>
                <th
                  scope="col"
                  title="One document of this template, to see what the layout actually looks like."
                >
                  Sample
                </th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {data.formats.map((f) => (
                <tr key={f.format_id}>
                  <td>
                    <div>{f.name}</div>
                    {/* The fingerprint is how two documents are known to be the
                        same paperwork, so it is worth being able to quote. */}
                    <div className="mono muted" style={{ fontSize: 11 }}>
                      {f.fingerprint.slice(0, 12)}
                    </div>
                  </td>
                  <td className="num">{fmtTokens(f.documents)}</td>
                  <td className="num">
                    {f.accuracy === null ? (
                      <span
                        className="muted"
                        title="No document of this format has been verified, so there is nothing to compare an extraction against. This is not 0%."
                      >
                        —
                      </span>
                    ) : (
                      <>
                        <span
                          className={
                            f.accuracy >= 90 ? "req-ok" : f.accuracy >= 50 ? "req-warn" : "req-bad"
                          }
                        >
                          {f.accuracy}%
                        </span>
                        {/* The denominator sits with the number. "100%" over
                            three fields is not a claim about a template, and a
                            reader who cannot see that has no way to know. */}
                        <div className="muted" style={{ fontSize: 11 }}>
                          {f.matched}/{f.compared} fields
                        </div>
                      </>
                    )}
                  </td>
                  <td className="num">
                    {f.required_score === null ? (
                      <span className="muted">—</span>
                    ) : (
                      <span
                        className={
                          f.required_score >= 90
                            ? "req-ok"
                            : f.required_score >= 50
                              ? "req-warn"
                              : "req-bad"
                        }
                      >
                        {f.required_score}%
                      </span>
                    )}
                  </td>
                  {/* Which field to go and fix. A format scoring 23% tells you
                      something is wrong; this tells you what, and it is almost
                      always one field rather than the template as a whole. */}
                  <td>
                    {f.wrong_fields.length ? (
                      <span className="pills" title="Fields this format gets wrong against verified truth">
                        {f.wrong_fields.slice(0, 3).map((w) => (
                          <span key={w} className="pill pill-critical">{w}</span>
                        ))}
                      </span>
                    ) : f.by_field.length && f.by_field[0].rate !== null && f.by_field[0].rate < 100 ? (
                      <span
                        className="pill pill-warning"
                        title={`Produced in ${f.by_field[0].present} of ${f.by_field[0].runs} documents of this format. Missing, not necessarily wrong — nothing here is verified.`}
                      >
                        {f.by_field[0].field} {f.by_field[0].rate}%
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num muted">
                    {f.avg_confidence === null ? "—" : `${f.avg_confidence}%`}
                  </td>
                  <td className="num">
                    {f.verified_documents ? (
                      f.verified_documents
                    ) : (
                      <span className="muted">none</span>
                    )}
                  </td>
                  <td className="nowrap muted">{fmtDate(f.last_seen)}</td>
                  {/* Opens the document itself, where the original scan is
                      rendered beside what was extracted from it. A format is a
                      fingerprint and a number shape; one look at an example
                      says more than either. */}
                  <td className="nowrap">
                    {f.sample_document_id ? (
                      <Link
                        to={`/documents/${f.sample_document_id}`}
                        title={f.sample_filename ?? "Open an example of this template"}
                      >
                        view sample
                      </Link>
                    ) : (
                      <span className="muted" title="No document of this format still has its file in storage.">
                        —
                      </span>
                    )}
                  </td>
                  <td className="nowrap">
                    <Link to={`/documents?format_id=${encodeURIComponent(f.format_id)}`}>
                      {f.documents === 1 ? "review 1 doc" : `review ${f.documents} docs`}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="muted" style={{ fontSize: 12.5, marginTop: 14 }}>
          Formats with no verified documents have nothing to learn from yet. Verifying
          a few of the worst-scoring template is worth more than verifying many of one
          that already scores well.
        </p>
      </div>
    </div>
  );
}
